
import { useState } from 'react';

interface TestimonialsSectionProps {
  language: 'es' | 'en';
}

export default function TestimonialsSection({ language }: TestimonialsSectionProps) {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const content = {
    es: {
      title: 'Lo que dicen nuestros clientes',
      subtitle: 'Testimonios reales de personas que confiaron en nosotros',
      testimonials: [
        {
          name: 'María González',
          country: '🇲🇽 México',
          text: 'Gracias a First Step Japan hice todos mis trámites en un solo día. Juampi me acompañó a cada oficina y tradujo todo perfectamente. ¡Increíble servicio!',
          rating: 5,
          pack: 'Municipal Pack'
        },
        {
          name: 'Carlos Rodríguez',
          country: '🇨🇴 Colombia',
          text: 'Me recibieron en el aeropuerto y entendí todo sin estrés. El proceso fue súper smooth y ahora ya tengo trabajo en Tokio. 100% recomendado.',
          rating: 5,
          pack: 'Premium Step Pack'
        },
        {
          name: 'Ana Martínez',
          country: '🇦🇷 Argentina',
          text: 'Juampi me ayudó como si fuera un amigo. Su experiencia personal hace toda la diferencia. Ahora me siento completamente integrada en Osaka.',
          rating: 5,
          pack: 'Life Start Pack'
        },
        {
          name: 'Diego Silva',
          country: '🇨🇱 Chile',
          text: 'El CV japonés que me armaron fue perfecto y conseguí trabajo en 2 semanas. El hanko y la SIM también los gestionaron súper rápido.',
          rating: 5,
          pack: 'Life Start Pack'
        }
      ]
    },
    en: {
      title: 'What our clients say',
      subtitle: 'Real testimonials from people who trusted us',
      testimonials: [
        {
          name: 'María González',
          country: '🇲🇽 Mexico',
          text: 'Thanks to First Step Japan I completed all my procedures in one day. Juampi accompanied me to each office and translated everything perfectly. Incredible service!',
          rating: 5,
          pack: 'Municipal Pack'
        },
        {
          name: 'Carlos Rodríguez',
          country: '🇨🇴 Colombia',
          text: 'They picked me up at the airport and I understood everything without stress. The process was super smooth and now I already have a job in Tokyo. 100% recommended.',
          rating: 5,
          pack: 'Premium Step Pack'
        },
        {
          name: 'Ana Martínez',
          country: '🇦🇷 Argentina',
          text: 'Juampi helped me as if he were a friend. His personal experience makes all the difference. Now I feel completely integrated in Osaka.',
          rating: 5,
          pack: 'Life Start Pack'
        },
        {
          name: 'Diego Silva',
          country: '🇨🇱 Chile',
          text: 'The Japanese CV they created for me was perfect and I got a job in 2 weeks. The hanko and SIM were also managed super quickly.',
          rating: 5,
          pack: 'Life Start Pack'
        }
      ]
    }
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === content[language].testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === 0 ? content[language].testimonials.length - 1 : prev - 1
    );
  };

  const currentData = content[language].testimonials[currentTestimonial];

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-yellow-50 to-red-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4">
              {content[language].title} ⭐
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content[language].subtitle}
            </p>
          </div>

          {/* Testimonial Carousel */}
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-4xl mx-auto">
              {/* Stars */}
              <div className="flex justify-center mb-6">
                {[...Array(currentData.rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-2xl"></i>
                ))}
              </div>

              {/* Testimonial Text */}
              <blockquote className="text-xl md:text-2xl text-gray-700 text-center leading-relaxed mb-8 italic">
                "{currentData.text}"
              </blockquote>

              {/* Client Info */}
              <div className="text-center">
                <h4 className="text-xl font-bold text-red-600 mb-2">
                  {currentData.name}
                </h4>
                <p className="text-gray-600 mb-2">{currentData.country}</p>
                <span className="inline-block bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-medium">
                  {currentData.pack}
                </span>
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 w-12 h-12 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center shadow-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 w-12 h-12 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center shadow-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {content[language].testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-colors cursor-pointer ${
                  index === currentTestimonial ? 'bg-red-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* All Testimonials Grid (Mobile) */}
          <div className="grid md:hidden gap-6 mt-12">
            {content[language].testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 text-center mb-4 italic">
                  "{testimonial.text}"
                </p>
                <div className="text-center">
                  <h4 className="font-bold text-red-600">{testimonial.name}</h4>
                  <p className="text-gray-600 text-sm">{testimonial.country}</p>
                  <span className="inline-block bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-medium mt-2">
                    {testimonial.pack}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
